import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import { tableHelper } from '@builttocreate/engine-utils';

import TableEditorHeader from './TableEditorHeader';
import TableEditorRow from './TableEditorRow';
import Button from '../../misc/Button';

import FieldTableCustomColumnIds from '../../../constants/FieldTableCustomColumnIds';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';

const {
  getRows,
  sortRows,
  getColumns,
  sortColumns,
  getColumnOrder,
} = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items:baseline;
  overflow: auto;
`;

const StyledTable = styled.div`
  display: flex;
  overflow: auto;
  flex-direction: column;
`;

const StyledRowsWrapper = styled.div`
  display: flex;
  max-height: 200px;
  overflow-y: auto;
  flex-direction: column;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 8px;
  margin-left: 26px;
`;

class TableEditor extends PureComponent {

  static propTypes = {

    id: PropTypes.string,
    fieldId: PropTypes.string, //fieldPosition Id
    focused: PropTypes.string,

    activeFieldPosition: PropTypes.object,

    onRowCreate: PropTypes.func,
    onColumnCreate: PropTypes.func,
    onColumnEdit: PropTypes.func,
    onColumnMoveLeft: PropTypes.func,
    onColumnMoveRight: PropTypes.func,

  }

  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      columns: [],
    };
  }

  componentDidMount() {
    this.populateTableData();
  }

  componentDidUpdate(prevProps) {

    const { activeFieldPosition } = this.props;

    if (activeFieldPosition && activeFieldPosition !== prevProps.activeFieldPosition) {
      this.populateTableData();
    }

  }

  populateTableData = () => {

    const { activeFieldPosition } = this.props;
    const { value, rowOrder, tableColumns, tableColumnOrder } = activeFieldPosition || {};

    const formattedRows = this.getFormattedAndSortedRows(value, rowOrder);
    const formattedColumns = this.getFormattedAndSortedColumns(tableColumns, tableColumnOrder);

    this.setState({ rows: formattedRows, columns: formattedColumns });

  }

  render() {

    const { columns, rows } = this.state;
    const { id, activeFieldPosition, focused, disableDragAndDrop } = this.props;

    return (
      <StyledWrapper>
        <StyledTable data-testid={`${id}-table-editor-table`}>
          <TableEditorHeader
            id={id}
            columns={columns}
            onEdit={this.handleColumnEdit}
            onMoveLeft={this.handleColumnMoveLeft}
            onMoveRight={this.handleColumnMoveRight}
          />
          <StyledRowsWrapper>
            {rows.map((row, rowIndex) => (
              <div key={`${row._id}-${rowIndex}`} id="table-editor-row">
                <TableEditorRow
                  id={id}
                  activeFieldPosition={activeFieldPosition}
                  key={`${row._id}-${rowIndex}`}
                  focused={focused}
                  row={row}
                  rowIndex={rowIndex}
                  columns={columns}
                  disableDragAndDrop={disableDragAndDrop}
                  onDelete={rows.length < 2 ? null : this.handleRowDelete}
                />
              </div>
            ))}
          </StyledRowsWrapper>
          <StyledFooter>
            <Button
              text="Row"
              icon={faPlus}
              styles={{ marginRight: 8 }}
              onClick={this.handleRowCreate}
            />
            <Button
              text="Text Column"
              icon={faPlus}
              styles={{ marginRight: 8 }}
              onClick={() => this.handleColumnCreate(FieldTableColumnTypes.text)}
            />
            <Button
              text="Dropdown Column"
              icon={faPlus}
              styles={{ marginRight: 8 }}
              onClick={() => this.handleColumnCreate(FieldTableColumnTypes.dropdown)}
            />
            <Button
              text="Image Column"
              icon={faPlus}
              onClick={() => this.handleColumnCreate(FieldTableColumnTypes.image)}
            />
          </StyledFooter>
        </StyledTable>
      </StyledWrapper>
    );

  }

  handleRowCreate = () => {

    const { onRowCreate, fieldId } = this.props;
    onRowCreate(fieldId);

  }

  handleRowDelete = (rowId, rowIndex) => {

    const { onRowDelete, fieldId } = this.props;
    onRowDelete(fieldId, rowId, rowIndex);

  }

  handleColumnCreate = (type) => {

    const { onColumnCreate, fieldId } = this.props;
    onColumnCreate(fieldId, type);

  }

  handleColumnEdit = (columnId) => {

    const { onColumnEdit, fieldId } = this.props;
    onColumnEdit(fieldId, columnId);

  }

  handleColumnMoveRight = (columnId) => {

    const { onColumnMoveRight, fieldId } = this.props;
    onColumnMoveRight(fieldId, columnId);

  }

  handleColumnMoveLeft = (columnId) => {

    const { onColumnMoveLeft, fieldId } = this.props;
    onColumnMoveLeft(fieldId, columnId);

  }

  getFormattedAndSortedColumns = (tableColumns, tableColumnOrder) => {
    if (!tableColumns) return [];

    const columnOrder = getColumnOrder(tableColumnOrder, tableColumns);
    const nextColumns = getColumns(sortColumns(tableColumns, columnOrder));

    //Add row number
    if (nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowNumber) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowNumber,
        type: FieldTableCustomColumnIds.rowNumber,
        width: 24,
      });
    }

    //Add row settings
    if (nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowMenu) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowMenu,
        type: FieldTableCustomColumnIds.rowMenu,
        width: 24,
      });
    }

    return nextColumns;

  }

  getFormattedAndSortedRows = (rows, rowOrder) => {
    return sortRows(getRows(rows), rowOrder);
  }

}

export default TableEditor;
